import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { fmtDate, fmtGNF } from "@/lib/format";
import { ABtn, Panel } from "./AdminLayout";

const STATUS_STYLE = {
  "en attente": "border-amber-700 bg-amber-950 text-amber-300",
  acceptée: "border-sky-700 bg-sky-950 text-sky-300",
  refusée: "border-rose-700 bg-rose-950 text-rose-300",
  reçue: "border-emerald-700 bg-emerald-950 text-emerald-300",
};

export default function AdminReturns() {
  const [returns, setReturns] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/returns");
      setReturns(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (r, status) => {
    const note = status === "refusée" ? window.prompt("Motif du refus (visible par le client)") : null;
    if (status === "refusée" && note === null) return;
    try {
      await api.put(`/admin/returns/${r.id}`, { status, note });
      toast.success(`Retour ${r.number} : ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const refund = async (r) => {
    try {
      await api.put(`/admin/returns/${r.id}/refund`);
      toast.success("Remboursement enregistré");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel
      title="Retours clients"
      subtitle={`${returns.length} demande(s) · retour accepté sous 48 h si l'article est en bon état`}
      testid="admin-returns-panel"
    >
      {returns.length === 0 && <p className="text-sm text-slate-400">Aucune demande de retour.</p>}

      <div className="mb-4 max-w-sm">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Scanner ou saisir le numéro RET-…"
          className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-gold"
          data-testid="returns-search-input"
        />
      </div>

      <div className="space-y-3">
        {returns
          .filter((r) =>
            !filter.trim() ||
            r.number.toLowerCase().includes(filter.trim().toLowerCase()) ||
            r.order_number.toLowerCase().includes(filter.trim().toLowerCase()),
          )
          .map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-950 p-4" data-testid={`return-row-${r.number}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-num text-sm font-bold text-white">{r.number}</p>
                <p className="text-xs text-slate-400">
                  Commande {r.order_number} · {r.customer_name} · {r.phone} · {fmtDate(r.created_at)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`ds-chip ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                <span className="ds-chip border-slate-700 bg-slate-900 text-slate-300">{r.refund_status}</span>
                <span className="font-num text-sm font-bold text-gold">{fmtGNF(r.refund_amount)}</span>
              </div>
            </div>

            <ul className="mt-2 space-y-1 text-sm text-slate-200">
              {r.items.map((it, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span>{it.product_name} <span className="font-num text-slate-500">{it.sku}</span></span>
                  <span className="font-num">× {it.quantity}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-slate-400">Motif : {r.reason}</p>

            <div className="mt-3 flex flex-wrap gap-2">
              {r.status !== "refusée" && (
                <a
                  href={`${API}/returns/${r.id}/label`}
                  target="_blank"
                  rel="noreferrer"
                  className="ds-btn-ghost border-slate-700 bg-slate-900 py-2 text-xs text-slate-200"
                  data-testid={`return-label-${r.number}`}
                >
                  Imprimer l'étiquette
                </a>
              )}
              {r.status === "en attente" && (
                <>
                  <ABtn variant="gold" onClick={() => decide(r, "acceptée")} data-testid={`return-accept-${r.number}`}>
                    Accepter
                  </ABtn>
                  <ABtn variant="danger" onClick={() => decide(r, "refusée")} data-testid={`return-reject-${r.number}`}>
                    Refuser
                  </ABtn>
                </>
              )}
              {r.status === "acceptée" && (
                <ABtn onClick={() => decide(r, "reçue")} data-testid={`return-receive-${r.number}`}>
                  Article reçu en bon état — remettre en stock
                </ABtn>
              )}
              {r.status === "reçue" && r.refund_status !== "remboursé" && (
                <ABtn variant="gold" onClick={() => refund(r)} data-testid={`return-refund-${r.number}`}>
                  Marquer comme remboursé
                </ABtn>
              )}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
